import { FormEvent, useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Actions } from "@twilio/flex-ui";
import Debug from "debug";

import pkg from "../package.json";

import { requestAuth, requestUpscope } from "./upscope-client";
import { UpscopeEnv } from "./utils";

import "./Upscope.css";

import { UpscopeServerError, UpscopeSuccessResponse, VisitorWithHistory } from "~integration/types/integration-objects";

const debug = Debug("upscope:widget");

type Props = { authUrl: string; upscopeEnv: UpscopeEnv };

function UpscopeWidget({ authUrl, upscopeEnv }: Props) {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [lookupCodeInput, setLookupCodeInput] = useState<string>();
  const [lookupCodeErrorMessage, setLookupCodeErrorMessage] = useState<string>();
  const [showLookupCodeInput, setShowLookupCodeInput] = useState<boolean>(false);
  const [triedVisitorSearch, setTriedVisitorSearch] = useState<boolean>(false);
  const [visitor, setVisitor] = useState<VisitorWithHistory | undefined>();
  const [watchLink, setWatchLink] = useState<string>();
  const [conversationSid, setConversationSid] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [integrationToken, setIntegrationToken] = useState<string>();

  useEffect(() => {
    requestAuth(authUrl).then(({ integrationToken }) => setIntegrationToken(integrationToken));
  }, [authUrl]);

  if (["staging", "production"].includes(upscopeEnv)) {
    Sentry.init({
      dsn: "https://e96ba443f2d74485ae84af150dd77253@o73137.ingest.sentry.io/4504889193136128",
      integrations: [Sentry.browserTracingIntegration()],
      environment: upscopeEnv,
      release: pkg.version,
    });
  }

  function onScreenShare(event: React.MouseEvent): void {
    event.preventDefault();
    window.open(watchLink ?? undefined);
  }

  const onSuccessRequest = ({ watchLink, visitor }: UpscopeSuccessResponse) => {
    debug("Got watch link: %s and visitor: %o", watchLink, visitor);
    setWatchLink(watchLink);
    setVisitor(visitor);
    setLoading(false);
  };

  const onErrorRequest = (error: UpscopeServerError) => {
    setLoading(false);
    debug("Error request: %o", error);

    if (typeof error.response === "undefined") {
      debug("Upscope internal error: %O", error);
      setErrorMessage("Upscope is currently not available");
      Sentry.captureException(error);
      return;
    }

    if (error.response.error === "integration_not_found") {
      debug("Upscope unauthorized error response: %o", error.response);
      setErrorMessage("Contact your team admin to setup the integration");
      return;
    }

    if (error.response.error === "not_found") {
      debug("Error with search: %o", error.response);
      const { message, tryLookupCode } = error.response;
      setShowLookupCodeInput(tryLookupCode);
      setErrorMessage(tryLookupCode ? undefined : message);
      setLookupCodeErrorMessage(tryLookupCode ? message : undefined);
    } else {
      setErrorMessage("An unexpected error occured");
      Sentry.captureException(error);
    }
  };

  async function onLookupCodeSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loading) return;

    if (lookupCodeInput && conversationSid && integrationToken) {
      requestUpscope({
        searchMethod: "from_lookup_code",
        request: {
          lookupCode: lookupCodeInput,
          conversationId: conversationSid,
        },
        integrationToken,
        upscopeEnv,
      })
        .then(({ watchLink, visitor }) => {
          onSuccessRequest({ watchLink, visitor });
          window.open(watchLink);
        })
        .catch(onErrorRequest);
    }
  }

  function onLookupCodeInput(event: React.ChangeEvent<HTMLInputElement>) {
    setLookupCodeInput(event.target.value.replace(/\D/g, ""));
  }

  const newAppInstance = useCallback(
    async (payload: Record<string, any>) => {
      setLoading(true);

      if ("task" in payload && typeof payload.task === "undefined") return;

      const conversationData = payload.task._task.attributes;
      const workerData = payload.task._task._worker.attributes;

      const customerEmail = conversationData.customerEmail;
      setConversationSid(() => conversationData.conversationSid);

      const agentName = workerData.full_name;
      const agentEmail = workerData.email;

      if (conversationSid && integrationToken) {
        requestUpscope({
          searchMethod: "from_visitor_details",
          request: {
            email: customerEmail,
            integrationId: `twilio_flex:${conversationSid}`,
            agentName,
            agentEmail,
            conversationId: conversationSid,
          },
          integrationToken,
          upscopeEnv,
        })
          .then(onSuccessRequest)
          .catch(onErrorRequest);
      }
    },
    [conversationSid, integrationToken, upscopeEnv],
  );

  useEffect(() => {
    Actions.replaceAction("WrapupTask", async (payload, original) => {
      setTriedVisitorSearch(false);
      setWatchLink("");
      setShowLookupCodeInput(false);
      setLookupCodeInput("");
      setErrorMessage("");
      setLookupCodeErrorMessage("");
      setVisitor(undefined);

      await original(payload);
    });

    Actions.addListener("afterSelectTask", newAppInstance);
  }, [newAppInstance]);

  return (
    <div className="UpscopeWidget__Container">
      <div className="UpscopeWidget__Logo" />
      {(() => {
        if (!triedVisitorSearch) {
          return (
            <div className="UpscopeWidget__Alert">
              <p>Please, select a conversation to start Upscope.</p>
            </div>
          );
        }

        if (watchLink && visitor) {
          return (
            <div className="UpscopeWidget__Screenshare__Container">
              <div>Screen share available</div>
              <button className="UpscopeWidget__Button" onClick={onScreenShare}>
                Screen Share now
              </button>
            </div>
          );
        }

        if (showLookupCodeInput) {
          return (
            <div>
              {lookupCodeErrorMessage && (
                <div className="UpscopeWidget__Alert">
                  <p>{lookupCodeErrorMessage}</p>
                </div>
              )}
              <form onSubmit={onLookupCodeSearch}>
                <input
                  className="UpscopeWidget__Input"
                  autoFocus
                  type="text"
                  placeholder="1234"
                  value={lookupCodeInput ?? undefined}
                  onChange={onLookupCodeInput}
                />
                <button className="UpscopeWidget__Button" type="submit">
                  Screen share
                </button>
              </form>
              {errorMessage && (
                <div className="UpscopeWidget__Alert UpscopeWidget__Alert__Error">
                  <p>{errorMessage}</p>
                </div>
              )}
            </div>
          );
        }

        if (errorMessage) {
          return (
            <div>
              <div className="UpscopeWidget__Alert UpscopeWidget__Alert__Error">
                <p>{errorMessage}</p>
              </div>
            </div>
          );
        }
      })()}
    </div>
  );
}

export default UpscopeWidget;
